import React, {Fragment} from 'react'
import {findDOMNode} from 'react-dom'
import styles from './PhotoClusters.scss'
import {InteractiveMarker} from 'common/Map/InteractiveMarker'
import * as upgma from 'lib/UPGMACluster'
import _ from 'lodash'
import {getConsole} from 'lib/getConsole'
import {showGallery} from 'features/gallery/showGallery'
import {Image} from 'common/Image'
import {pointDistanceObject, pointAverageObject} from 'lib/geoUtils'
const console = getConsole('common', 'PhotoClusters')

const MAX_PHOTOS_IN_POPUP = 5

// optimization: using interpolation search could give us a big bump here
// since we know that timestamp increases in a semi-regular fashion
const getClosestPointByTimestamp = (points, timestamp) => {
  if (points.length === 1) {
    return points[0]
  }
  const pivot = Math.floor(points.length / 2)
  if (points[pivot].time > timestamp) {
    return getClosestPointByTimestamp(points.slice(0, pivot), timestamp)
  } else {
    return getClosestPointByTimestamp(points.slice(pivot), timestamp)
  }
}

// Base threshold off trip distance divided by 30.
// lower threshold at higher zoom levels to get more precise photo placement
// (values are experimentally determined)
// Safety fallback to 4km if NaN.
const zoomClusterThreshold = (distance, zoom) => {
  if (zoom >= 17) { return 1 }
  if (zoom >= 16) { return 50 }
  if (zoom >= 15) { return 100 }
  if (zoom >= 14) { return 140 }
  if (zoom >= 13) { return 175 }
  if (zoom >= 12) { return 420 }
  return distance / 30 || 4000
}

// TRIP photo clustering implmentation. Clusters photos buy guessing
// lat/lng from the photo timestamp, mapping that to distance along route, then
// running UPGMA on those distance values. Runs fast because we're just working
// with float data, and also eliminates lots of potential issues from phones
// doing wonky things with embedded photo lat/lng (there are tons of dumb
// edge cases around this)
const FIFTEEN_MINUTES = 15 * 60 * 1000
const clusterPhotosTrip = _.memoize(zoom => _.memoize(trip => _clusterPhotosTrip(zoom, trip), trip => trip && `${trip.id}-${trip.photos.length}`))
const _clusterPhotosTrip = (zoom, trip) => {
  console.log(`clusterPhotos cache miss for trip.id=${trip.id}`)
  if (trip.photos.length === 0) {
    return []
  }

  // sanity-check: only try to use timestamps to guess the location of
  // photos that were taken within 15 minutes of the trip start/end
  const tripStart = +Date.parse(trip.departedAt)
  const tripStartPadded = tripStart - FIFTEEN_MINUTES
  const tripEnd = tripStart + trip.metrics.duration * 1000
  const tripEndPadded = tripEnd + FIFTEEN_MINUTES
  const takenDuringTrip = p => {
    if (!p || !p.capturedAt) { return false }
    const timestamp = +Date.parse(p.capturedAt)
    if (!timestamp) { return false }
    return (timestamp > tripStartPadded) && (timestamp < tripEndPadded)
  }
  const validPhotos = trip.photos.filter(takenDuringTrip)

  // using geoUtils getNearestPoint doesn't work here because it leads to incorrect
  // distances in cases where the route doubles back on itself.
  // this is still a somewhat expensive way to do a time<>distance mapping,
  // and probably the #1 candidate for optimization if we have runtime perf
  // issues with this code
  const nearestPoints = validPhotos.map(p => {
    const timestamp = Math.floor(Date.parse(p.capturedAt) / 1000)
    return getClosestPointByTimestamp(trip.trackPoints, timestamp)
  }).filter(v => v)

  // get a mapping of distances to photo index:
  const distances = nearestPoints.map(p => p.distance)

  const threshold = zoomClusterThreshold(trip.distance, zoom)
  const clusters = upgma.cluster(distances, threshold)

  const photoClusters = clusters.map(c => {
    const photos = c.map(i => validPhotos[i])
    // We don't want to use pointAverageObject for position  here
    // because unlike in the route case where latLngs are gps-derived
    // and will typically cluster along the route, the points here are
    // just bits of the route line and averaging the points along a
    // (typically non-straight) route segment will cause the photo indicator
    // to float off to the side of the route line.
    //
    // Instead we just use the first point, which is a pretty good
    // approximation and saves a lot of time (we'd otherwise need to sort points
    // by distance for each cluster to find the median)
    const firstPointIndex = c[0]
    return {photos, position: nearestPoints[firstPointIndex]}
  })
  return photoClusters
}

// ROUTE photo clustering implementation. Since we don't have timestamps but
// we do have lat/lng data (in theory), run a geographically aware UPGMA
// clustering where we swap in functions to compute distance between lat/lng
// values and average those values.
//
// A possible quick-n-easy speedup here would be to pre-convert all the
// lat/lngs to radians *before* running the computation, which would
// eliminate a lot of converting back and forth in geoUtils.
const clusterPhotosRoute = _.memoize(zoom => _.memoize(route => _clusterPhotosRoute(zoom, route), route => route && `${route.id}-${route.photos.length}`))
const _clusterPhotosRoute = (zoom, route) => {
  console.log(`clusterPhotos cache miss for route.id=${route.id}`)
  if (route.photos.length === 0) {
    return []
  }
  // grab photos with a valid location
  const hasLocation = p => p && p.lat && p.lng
  const validPhotos = route.photos.filter(hasLocation)

  // in this case, we don't need a separate array for cluster data
  // since photos already duck-type as latLng objects
  const latLngs = validPhotos

  // Base threshold off route distance divided by 30.
  const threshold = zoomClusterThreshold(route.distance, zoom)
  const clusters = upgma.cluster(
    latLngs,
    threshold,
    {getDistanceFn: pointDistanceObject, getAvgFn: pointAverageObject}
  )

  const photoClusters = clusters.map(c => {
    const photos = c.map(i => validPhotos[i])
    const position = pointAverageObject(photos) // average photo positions to get location
    return {photos, position}
  })
  return photoClusters
}

// Render a layer of photo clusters for a give trip or route
export class PhotoClusters extends React.PureComponent {
  render () {
    const {troute, clusterStrategy, zoom} = this.props
    const clusters = clusterStrategy === 'trip'
      ? clusterPhotosTrip(zoom)(troute)
      : clusterPhotosRoute(zoom)(troute)
    return (
      <Fragment>
        {clusters.map(({photos, position}) =>
          <PhotoCluster key={photos[0].id}
            position={position}
            photos={photos}
            openGallery={(activePhotoId) => showGallery({photos, activePhotoId})} />)}
      </Fragment>
    )
  }
}

// Render a single cluster of photos, with hover preview
export const PhotoCluster = ({photos, position, openGallery}) => {
  const hoveredPopup = (
    <div className={styles.smallPopup}>
      {photos.slice(0, MAX_PHOTOS_IN_POPUP).map(p => <Image key={p.id}
        id={p.id}
        height={40}
        width={40} />)}
      {photos.length > MAX_PHOTOS_IN_POPUP &&
        <div
          className={styles.more}
          ref={component => component && findDOMNode(component).addEventListener('click', () => openGallery(photos[MAX_PHOTOS_IN_POPUP].id))}>
          <span>+{photos.length - MAX_PHOTOS_IN_POPUP}</span>
        </div>}
    </div>
  )

  const focusedPopup = (
    <div className={styles.bigPopup}>
      {photos.slice(0, MAX_PHOTOS_IN_POPUP).map(p => <Image key={p.id}
        id={p.id}
        // we have to use native click events because
        // of map stopPropagation shenanigans
        ref={component => component && findDOMNode(component).addEventListener('click', () => openGallery(p.id))}
        height={100}
        width={100} />)}
      {photos.length > MAX_PHOTOS_IN_POPUP &&
        <a
          className={styles.more}
          ref={component => component && findDOMNode(component).addEventListener('click', () => openGallery(photos[MAX_PHOTOS_IN_POPUP].id))}>
          <span>+{photos.length - MAX_PHOTOS_IN_POPUP}</span>
        </a>}
    </div>
  )

  return (
    <InteractiveMarker
      position={position}
      icon='photo'
      id={`cluster-${position.lat}-${position.lng}`}
      hoveredPopup={hoveredPopup}
      focusedPopup={focusedPopup}
      popupOffset={[0, -35]} />
  )
}
